// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset } from '../types';
import { num, delta, people, esc, CHANNEL_COLOUR, REMOTENESS_ORDER, REMOTENESS_SHORT, REMOTENESS_COLOUR } from '../format';
import { gloss } from '../glossary';
import { stackedArea } from '../charts';
import { navigate } from '../main';

export function renderOverview(root: HTMLElement, data: Dataset): void {
  const { national, meta } = data;
  const c = meta.counts;
  const N = national.years.length;
  const years = national.years;

  // substitution area: branch, bankpost, other (the human channels)
  const areaSeries = [
    { key: 'branch', label: 'Branch', colour: CHANNEL_COLOUR.branch, values: national.series.branch },
    { key: 'bankpost', label: 'Bank@Post', colour: CHANNEL_COLOUR.bankpost, values: national.series.bankpost },
    { key: 'other', label: 'Other face-to-face', colour: CHANNEL_COLOUR.other, values: national.series.other },
  ];

  // remoteness branch % change bars
  const remBars = REMOTENESS_ORDER.map((k) => {
    const s = national.byRemoteness[k];
    const ch = s && s.branch[0] ? (s.branch[N - 1] - s.branch[0]) / s.branch[0] : 0;
    return { k, ch, first: s?.branch[0] ?? 0, last: s?.branch[N - 1] ?? 0 };
  });
  const remMax = Math.max(...remBars.map((r) => Math.abs(r.ch)), 0.01);

  root.innerHTML = `
    <section class="hero">
      <div class="kicker">Physical banking in Australia · ${years[0]}–${years[N - 1]}</div>
      <h2>Australia has closed <span class="big">${num(c.branches2017 - c.branchesNow)}</span> bank branches — <span class="big">${delta(c.branchChangePct)}</span> — in eight years.</h2>
      <p>Every branch, ${gloss('bankpost', 'Bank@Post')}, other counter and bank ${gloss('atm', 'ATM')} in the country, from APRA's annual points-of-presence return. As branches vanished, one thing barely moved — and it isn't a bank.</p>
    </section>

    <div class="stat-row">
      <div class="stat"><div class="stat-label">Branches now</div><div class="stat-value">${num(c.branchesNow)}</div><div class="stat-note">${num(c.branches2017)} in ${years[0]} · <span class="down">${delta(c.branchChangePct)}</span></div></div>
      <div class="stat"><div class="stat-label">Bank-owned ATMs</div><div class="stat-value">${num(c.atmNow)}</div><div class="stat-note">${num(c.atm2017)} in ${years[0]} · <span class="down">${delta(c.atmChangePct)}</span></div></div>
      <div class="stat"><div class="stat-label">Bank@Post outlets</div><div class="stat-value">${num(c.bankpostNow)}</div><div class="stat-note">${num(c.bankpost2017)} in ${years[0]} · ${delta(c.bankpostChangePct)}</div></div>
      <div class="stat hl"><div class="stat-label">Towns that lost their last branch</div><div class="stat-value">${num(c.sa2LostLastCount)}</div><div class="stat-note">~${people(c.popAffected)} residents affected</div></div>
      <div class="stat hl"><div class="stat-label">Left with no staffed banking</div><div class="stat-value">${num(c.sa2LostLastNothing)}</div><div class="stat-note">no branch and no Bank@Post</div></div>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h3>The great substitution</h3>
        <p>Staffed banking points by type, each June. The navy branch band drains away — but the teal ${gloss('bankpost', 'Bank@Post')} band holds almost flat, so in hundreds of towns the post office counter is quietly replacing the bank.</p>
      </div>
      <div class="chart-wrap">${stackedArea(years, areaSeries, { w: 860, h: 340 })}</div>
      <div class="legend">
        ${areaSeries.map((s) => `<span class="legend-item"><span class="legend-swatch" style="background:${s.colour}"></span>${s.label}</span>`).join('')}
      </div>
    </div>

    <div class="two-col">
      <div class="panel">
        <div class="panel-head">
          <h3>Who is losing branches fastest</h3>
          <p>Change in branches since ${years[0]}, by ABS ${gloss('remoteness', 'remoteness')}. The cities lost the most branches; the bush lost the largest <em>share</em> — where there was least to spare.</p>
        </div>
        <div class="barlist">
          ${remBars.map((r) => `
            <button class="barrow" data-rem="${esc(r.k)}" aria-label="${esc(REMOTENESS_SHORT[r.k])}: ${delta(r.ch)}">
              <span class="lbl">${esc(REMOTENESS_SHORT[r.k] ?? r.k)}</span>
              <span class="track"><span style="width:${((Math.abs(r.ch) / remMax) * 100).toFixed(1)}%;background:${REMOTENESS_COLOUR[r.k]}"></span></span>
              <span class="val down">${delta(r.ch)}</span>
            </button>`).join('')}
        </div>
        <p class="mini-note">${num(remBars[0].first)} → ${num(remBars[0].last)} branches in Major Cities. Click a bar for the remoteness view.</p>
      </div>

      <div class="panel">
        <div class="panel-head">
          <h3>What this site can tell you</h3>
          <p>Nine ways to read the retreat of physical banking.</p>
        </div>
        <div class="barlist" style="gap:8px">
          <button class="barrow" data-go="map" style="grid-template-columns:1fr auto"><span class="lbl">🗺️ Every branch on a map — and per-person access by region</span><span class="val">Map →</span></button>
          <button class="barrow" data-go="deserts" style="grid-template-columns:1fr auto"><span class="lbl">🏜️ The ${num(c.sa2LostLastCount)} towns that lost their last branch</span><span class="val">Deserts →</span></button>
          <button class="barrow" data-go="banks" style="grid-template-columns:1fr auto"><span class="lbl">🏦 Which banks shut the most branches</span><span class="val">Banks →</span></button>
          <button class="barrow" data-go="remoteness" style="grid-template-columns:1fr auto"><span class="lbl">📶 Is remote Australia losing access faster?</span><span class="val">Remoteness →</span></button>
          <button class="barrow" data-go="rankings" style="grid-template-columns:1fr auto"><span class="lbl">📊 Rank every region by access and by loss</span><span class="val">Rankings →</span></button>
          <button class="barrow" data-go="explorer" style="grid-template-columns:1fr auto"><span class="lbl">🔎 Search any region or bank</span><span class="val">Explorer →</span></button>
        </div>
      </div>
    </div>
  `;

  root.querySelectorAll<HTMLButtonElement>('[data-rem]').forEach((b) =>
    b.addEventListener('click', () => navigate({ view: 'remoteness' as never })),
  );
  root.querySelectorAll<HTMLButtonElement>('[data-go]').forEach((b) =>
    b.addEventListener('click', () => navigate({ view: b.dataset.go as never })),
  );
}
